import { Terminal, Shield, BookOpen } from "lucide-react";

const MARQUEE_ITEMS = [
  "BLEEDING-EDGE PROJECT EXCHANGE",
  "HONEST ETHICS-OF-AI BREAKOUTS",
  "PEER CODE ARCHITECTURE",
  "PRIVATE GUEST WRITING & DISPATCHES",
  "ZERO FLUFF PRODUCT REVIEWS",
  "MONTHLY ZOOM ROUNDTABLES",
];

export function Marquee() {
  const items = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS];
  return (
    <div
      className="border-y border-slate-200 bg-slate-50 py-4 overflow-hidden select-none"
      data-testid="topics-marquee"
    >
      <div className="aix-mq font-mono-lbl text-xs text-slate-500 tracking-[0.25em] uppercase flex gap-12 items-center">
        {items.map((item, i) => (
          <span key={i} className={i % 2 === 1 ? "text-aix-cyandeep" : ""}>
            • {item}
          </span>
        ))}
      </div>
    </div>
  );
}

const PILLARS = [
  {
    tag: "AGENTIC REPOS",
    title: "Bleeding-Edge Exchange",
    body: "Direct teardowns of internal AI infrastructure, evaluation harnesses, and inference optimization workflows.",
    img: "https://images.unsplash.com/photo-1773349807434-374473797148?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxkZXZlbG9wZXIlMjBjb2RpbmclMjBkYXJrJTIwbW9kZXxlbnwwfHx8fDE3OTIwMTI4MTB8MA&ixlib=rb-4.1.0&q=85",
    alt: "Developer terminal displaying real autonomous agent workflows",
    Icon: Terminal,
    anim: "aix-card-1",
    testid: "pillar-card-exchange",
  },
  {
    tag: "CLOSED SESSIONS",
    title: "Ethics & Alignment Debates",
    body: "Unfiltered, off-the-record discussions on autonomous agent risks, corporate liability, and governance.",
    img: "https://images.unsplash.com/photo-1653549893012-b8b4fbe97630?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhdGElMjBuZXR3b3JrfGVufDB8fHxibHVlfDE3OTIwMTI4MTB8MA&ixlib=rb-4.1.0&q=85",
    alt: "Abstract data network with cyan lines representing AI governance",
    Icon: Shield,
    anim: "aix-card-2",
    testid: "pillar-card-ethics",
  },
  {
    tag: "MEMBERS ONLY",
    title: "Dispatches & Guest Writing",
    body: "Co-authored technical blueprints and private thought leadership circulated across the member network.",
    img: "https://images.unsplash.com/photo-1600132806370-bf17e65e942f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxibHVlcHJpbnQlMjBkb2N1bWVudHxlbnwwfHx8fDE3OTIwMTI4MTd8MA&ixlib=rb-4.1.0&q=85",
    alt: "Editorial dispatch layout on technical AI architecture",
    Icon: BookOpen,
    anim: "aix-card-3",
    testid: "pillar-card-dispatches",
  },
];

export function Pillars() {
  return (
    <section id="focus" className="py-24 md:py-32 max-w-6xl mx-auto px-6 scroll-mt-20" data-testid="pillars-section">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-4">
        <div>
          <span className="font-mono-lbl text-xs text-aix-cyandeep tracking-widest uppercase">01 / PILLARS</span>
          <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight text-slate-900 mt-2">
            What Members Exchange
          </h2>
        </div>
        <p className="text-slate-500 max-w-md text-sm leading-relaxed" data-testid="pillars-subtitle">
          For practitioners building AI software, agents, and workflows. No generic summaries or vendor pitches.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {PILLARS.map(({ tag, title, body, img, alt, Icon, anim, testid }) => (
          <div
            key={title}
            className={`bg-white border border-slate-200 shadow-sm rounded-xl p-6 flex flex-col justify-between group hover:border-aix-cyan/60 transition-colors ${anim}`}
            data-testid={testid}
          >
            <div className="rounded-lg h-44 mb-6 relative overflow-hidden">
              <img
                src={img}
                alt={alt}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#040812]/90 via-[#040812]/30 to-transparent"></div>
              <Icon className="absolute top-4 right-4 w-8 h-8 text-aix-cyan/80" />
              <span className="absolute bottom-4 left-4 font-mono-lbl text-[11px] text-aix-cyan bg-slate-950/80 px-2 py-1 rounded border border-aix-cyan/30">
                {tag}
              </span>
            </div>
            <div>
              <h3 className="font-display text-xl font-bold text-slate-900 mb-2">{title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{body}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

const AGENDA = [
  "20-minute teardown of a member's AI system",
  "30-minute small-group breakouts",
  "Off-the-record AI ethics debate",
];

export function Meetups() {
  return (
    <section
      id="meetups"
      className="py-24 bg-slate-50 border-y border-slate-200 relative scroll-mt-20"
      data-testid="meetups-section"
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="font-mono-lbl text-xs text-aix-cyandeep tracking-widest uppercase">02 / CADENCE</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-6">
              Monthly Zoom-First Roundtables
            </h2>
            <p className="text-slate-600 text-sm leading-relaxed mb-6">
              No travel required. Once a month, members meet on Zoom for a focused
              75-minute session on AI software development, agentic workflows, and
              the production systems behind them.
            </p>
            <ul className="space-y-3 font-mono-lbl text-xs text-slate-500">
              {AGENDA.map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-aix-cyan"></span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <div
              className="rounded-2xl p-8 border border-aix-cyan/40 relative overflow-hidden min-h-[320px] flex flex-col justify-between"
              data-testid="meetups-visual"
            >
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Members gathered around a screen reviewing a live application together"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-[#040914]/80 via-[#040914]/60 to-[#040914]/90"></div>

              <div className="relative z-10 flex justify-between items-center mb-16">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-rose-500 animate-pulse"></span>
                  <span className="font-mono-lbl text-xs text-white">MONTHLY FORMAT</span>
                </div>
                <span className="font-mono-lbl text-xs text-aix-cyan bg-[#071C3E]/85 px-3 py-1 rounded border border-aix-cyan/40">
                  ONLINE EVERYWHERE
                </span>
              </div>
              <div className="relative z-10 bg-black/60 backdrop-blur-md p-6 rounded-xl border border-cyan-950">
                <p className="font-mono-lbl text-[11px] text-aix-cyan uppercase tracking-widest mb-1">
                  Standard agenda
                </p>
                <h4 className="font-display text-lg font-bold text-white">Next Monthly Meetup</h4>
                <p className="text-xs text-slate-400 mt-1">
                  First Thursday of each month • 11:00 AM PST / 2:00 PM EST / 7:00 PM UTC
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
